/**
 * OrgConsultantsPanel — "Outside consultants" on the Settings page
 * (plans/CONSULTANT_CASE_ACCESS_PLAN.md §3.6). An org admin shares ONE case
 * with ONE person for a while, extends, or ends it. The person is never a
 * member of this org; they see this case and nothing else here.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';

const DEFAULT_DAYS = 90;

function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString() : '—';
}

function statusLabel(g) {
  if (g.status === 'revoked') return 'Ended';
  if (g.status === 'expired' || !g.is_currently_active) return 'Expired';
  return 'Active';
}

export default function OrgConsultantsPanel() {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [notice, setNotice] = useState(null);
  const [showEnded, setShowEnded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get('/org/consultants');
      setGrants(d.grants || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = grants.filter(g => g.status === 'active' && g.is_currently_active);
  const ended = grants.filter(g => !(g.status === 'active' && g.is_currently_active));
  const shown = showEnded ? grants : active;

  return (
    <div className="settings-group org-consultants-panel">
      <div className="settings-group-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 className="settings-group-title">Outside consultants{!loading && ` (${active.length})`}</h2>
        <button className="btn btn-sm btn-primary" onClick={() => { setShowShare(v => !v); setNotice(null); }}>
          {showShare ? 'Cancel' : '+ Share a case'}
        </button>
      </div>
      <p className="settings-hint" style={{ color: 'var(--text-muted, #6c757d)', fontSize: 12, margin: '0 0 8px' }}>
        Someone outside your office who works one case with you. They see that case and nothing else here,
        their access ends on a date you set, and everything they do is in your audit log under their own name.
      </p>

      {showShare && (
        <ShareForm onDone={(r) => { setShowShare(false); setNotice(r); load(); }} />
      )}

      {notice && (
        <div className="settings-message success" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{notice}</span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      )}

      {error && <p className="error-text">{error}</p>}
      {loading ? (
        <p className="loading-text">Loading…</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
          <table className="settings-table">
            <thead>
              <tr><th>Who</th><th>Case</th><th>Until</th><th>Issued by</th><th>Agreement</th><th></th></tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={6} className="empty-row">{showEnded ? 'No consultants yet' : 'No one outside your office has access right now'}</td></tr>
              )}
              {shown.map(g => <GrantRow key={g.id} grant={g} onChanged={load} onNotice={setNotice} />)}
            </tbody>
          </table>
          </div>
          {ended.length > 0 && (
            <button type="button" className="btn btn-sm btn-ghost" style={{ marginTop: 6 }} onClick={() => setShowEnded(v => !v)}>
              {showEnded ? 'Hide ended' : `Show ended (${ended.length})`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function GrantRow({ grant, onChanged, onNotice }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [extendDays, setExtendDays] = useState(DEFAULT_DAYS);
  const [showExtend, setShowExtend] = useState(false);
  const isActive = grant.status === 'active' && grant.is_currently_active;

  async function extend() {
    setBusy(true); setError(null);
    try {
      const r = await api.patch(`/org/consultants/${grant.id}`, { days: Number(extendDays) });
      onNotice(`${grant.display_name || grant.email} now has access until ${fmtDate(r.grant?.expires_at)}.`);
      setShowExtend(false);
      onChanged();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function end() {
    setBusy(true); setError(null);
    try {
      await api.del(`/org/consultants/${grant.id}`, { reason: 'Ended by org admin' });
      onNotice(`${grant.display_name || grant.email} no longer has access to ${grant.case_name || 'the case'}.`);
      onChanged();
    } catch (e) { setError(e.message); setBusy(false); setConfirmEnd(false); }
  }

  return (
    <tr style={isActive ? undefined : { opacity: 0.6 }}>
      <td>
        {grant.display_name || '—'}
        <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted, #6c757d)' }}>{grant.email}</div>
      </td>
      <td>{grant.case_name || grant.case_id}</td>
      <td title={grant.expires_at || ''} style={{ whiteSpace: 'nowrap' }}>
        {statusLabel(grant) === 'Active' ? fmtDate(grant.expires_at) : statusLabel(grant)}
      </td>
      <td>{grant.issued_by_kind === 'ops' ? 'ChargeStack (setup)' : (grant.issued_by_email || 'org admin')}</td>
      <td className="mono" style={{ fontSize: 12 }}>{grant.agreement_ref}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {isActive && (
          <div className="member-actions">
            {!showExtend && !confirmEnd && (
              <>
                <button className="btn btn-sm btn-ghost" onClick={() => setShowExtend(true)} disabled={busy}>Extend</button>
                <button className="btn btn-sm btn-ghost" onClick={() => setConfirmEnd(true)} disabled={busy}>End access</button>
              </>
            )}
            {showExtend && (
              <>
                <input className="form-input form-input-sm" type="number" min={1} max={365} value={extendDays}
                  onChange={e => setExtendDays(e.target.value)} style={{ width: 70 }} title="Days from today" />
                <button className="btn btn-sm btn-primary" onClick={extend} disabled={busy}>Set</button>
                <button className="btn btn-sm btn-ghost" onClick={() => setShowExtend(false)} disabled={busy}>Cancel</button>
              </>
            )}
            {confirmEnd && (
              <>
                <button className="btn btn-sm btn-danger" onClick={end} disabled={busy}>Confirm end</button>
                <button className="btn btn-sm btn-ghost" onClick={() => setConfirmEnd(false)} disabled={busy}>Cancel</button>
              </>
            )}
          </div>
        )}
        {error && <p className="error-text">{error}</p>}
      </td>
    </tr>
  );
}

function ShareForm({ onDone }) {
  const [cases, setCases] = useState([]);
  const [form, setForm] = useState({ email: '', case_id: '', days: DEFAULT_DAYS, reason: '', agreement_ref: '', attested_screening: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/cases', { limit: 100 }).then(d => {
      if (!alive) return;
      // Only this org's own cases can be shared (not ones shared with us).
      setCases((d.cases || []).filter(c => !c.access));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const r = await api.post('/org/consultants', { ...form, days: Number(form.days) });
      onDone(`${r.grant?.display_name || r.grant?.email} can now open ${r.grant?.case_name || 'the case'} until ${fmtDate(r.grant?.expires_at)}.`);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <form onSubmit={submit} className="org-invite-form" style={{ display: 'grid', gap: 8, gridTemplateColumns: '2fr 2fr 1fr', alignItems: 'end', margin: '8px 0 16px' }}>
      <div className="form-group">
        <label className="form-label">Their email *</label>
        <input className="form-input" type="email" required value={form.email} onChange={set('email')} placeholder="name@firm.com" />
      </div>
      <div className="form-group">
        <label className="form-label">Case *</label>
        <select className="form-input" required value={form.case_id} onChange={set('case_id')}>
          <option value="">Choose a case…</option>
          {cases.map(c => <option key={c.id} value={c.id}>{c.name}{c.case_number ? ` (#${c.case_number})` : ''}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Days of access</label>
        <input className="form-input" type="number" min={1} max={365} value={form.days} onChange={set('days')} />
      </div>
      <div className="form-group" style={{ gridColumn: '1 / 3' }}>
        <label className="form-label">Why *</label>
        <input className="form-input" required value={form.reason} onChange={set('reason')} placeholder="e.g. Organizing the discovery file for trial prep" />
      </div>
      <div className="form-group">
        <label className="form-label">Agreement reference *</label>
        <input className="form-input" required value={form.agreement_ref} onChange={set('agreement_ref')} placeholder="e.g. Engagement letter 2026-10-21" />
      </div>
      <label style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
        <input type="checkbox" checked={form.attested_screening} onChange={set('attested_screening')} required style={{ marginTop: 3 }} />
        <span>I confirm this person is screened per our organization's policy (for a criminal-justice agency: fingerprint-based background check on file and the CJIS Security Addendum signed).</span>
      </label>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Sharing…' : 'Share this case'}</button>
        <span className="settings-hint" style={{ color: 'var(--text-muted, #6c757d)', fontSize: 12 }}>
          They must already have a ChargeStack account with their own organization. They are not added to yours.
        </span>
      </div>
      {error && <p className="error-text" style={{ gridColumn: '1 / -1' }}>{error}</p>}
    </form>
  );
}
